// Rendering instants in the reader's own timezone.
//
// Every timestamp the API returns is an RFC3339 UTC instant
// ("2026-09-20T12:18:23.902764Z"). Slicing that string -- which is what the
// first version of these screens did -- prints the UTC wall clock while
// looking exactly like a local time, with no marker to say otherwise. In
// Asia/Calcutta that is five and a half hours wrong, and wrong in the
// direction that makes a live collector look stale.
//
// So: parse to a real Date, format through Intl in the browser's own
// timezone, and always render the zone name. `Intl.DateTimeFormat` with no
// locale argument follows the browser's own settings, which is the right
// default for an internal tool used from several countries.
//
// One thing deliberately does NOT go through here: `report_date`. That is a
// calendar date (`"2026-09-20"`), not an instant -- the day a report covers,
// which is the same day everywhere. Converting it to a local timezone would
// shift it by a day for anybody west of UTC and mean nothing.

const ABSOLUTE = new Intl.DateTimeFormat(undefined, {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZoneName: "short",
});

const DAY = new Intl.DateTimeFormat(undefined, {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const CLOCK = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
  timeZoneName: "short",
});

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

/** Largest-first, so the first unit the gap clears is the one used. */
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 31_536_000_000],
  ["month", 2_592_000_000],
  ["week", 604_800_000],
  ["day", 86_400_000],
  ["hour", 3_600_000],
  ["minute", 60_000],
  ["second", 1_000],
];

/** `null` for absent or unparseable, so callers render one "never" branch. */
export function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "20 Sep 2026, 17:48 GMT+5:30" */
export function formatInstant(iso: string | null | undefined, fallback = "—"): string {
  const d = parseInstant(iso);
  return d ? ABSOLUTE.format(d) : fallback;
}

/** "20 Sep 2026" -- the local day an instant fell on. */
export function formatDay(iso: string | null | undefined, fallback = "—"): string {
  const d = parseInstant(iso);
  return d ? DAY.format(d) : fallback;
}

/** "17:48:23 GMT+5:30" */
export function formatClock(iso: string | null | undefined, fallback = "—"): string {
  const d = parseInstant(iso);
  return d ? CLOCK.format(d) : fallback;
}

/**
 * "4 minutes ago". For "last polled", which is really asking whether the
 * collector is alive -- a question an absolute time makes you do arithmetic
 * to answer.
 */
export function formatRelative(
  iso: string | null | undefined,
  now: number = Date.now(),
  fallback = "never",
): string {
  const d = parseInstant(iso);
  if (!d) return fallback;

  const diff = d.getTime() - now;
  for (const [unit, ms] of UNITS) {
    if (Math.abs(diff) >= ms || unit === "second") {
      return RELATIVE.format(Math.round(diff / ms), unit);
    }
  }
  return "just now";
}

/**
 * Local time and the original UTC together, for a tooltip.
 *
 * The exact instant stays recoverable from the screen -- which matters when
 * somebody is comparing what they see against a log line or a database row,
 * both of which are in UTC.
 */
export function formatFull(iso: string | null | undefined): string | undefined {
  const d = parseInstant(iso);
  return d ? `${ABSOLUTE.format(d)}  ·  ${d.toISOString()}` : undefined;
}
